import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, updateUserProfile } from '../services/userService';
import { useAuth } from '../context/AuthContext';

const ProfileEditPage = () => {
  const { currentUser, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  
  const [profileImage, setProfileImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Mevcut profil bilgilerini yükle
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await getUserProfile();
        
        if (response.success) {
          const user = response.user;
          setFormData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            address: user.address || '',
          });
          
          if (user.profileImage) {
            setPreviewUrl(user.profileImage);
          }
        }
      } catch (error) {
        setError('Profil bilgileri yüklenirken bir hata oluştu');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, []);
  
  // Form alanları değişikliği
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Resim seçimi
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      // Önizleme için URL oluştur
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Form gönderimi
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // FormData oluştur
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('address', formData.address);
      
      if (profileImage) {
        data.append('profileImage', profileImage);
      }
      
      const response = await updateUserProfile(data);
      
      if (response.success) {
        setSuccess('Profil başarıyla güncellendi');
        if (typeof updateUser === 'function') {
          updateUser(response.user); // Context'teki kullanıcı bilgisini güncelle
        }
        
        // Kısa bir süre sonra profil sayfasına yönlendir
        setTimeout(() => {
          navigate('/profile');
        }, 2000);
      } else {
        setError(response.message || 'Profil güncellenirken bir hata oluştu');
      }
    } catch (error) {
      setError('Sunucu hatası: ' + (error.response?.data?.message || error.message));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Cloudinary için resim optimizasyonu
  const getOptimizedProfileImage = (url, size = 200) => {
    if (!url) return '';
    
    if (url.includes('cloudinary.com')) {
      try {
        const parts = url.split('/upload/');
        return `${parts[0]}/upload/w_${size},h_${size},c_fill,g_face/${parts[1]}`;
      } catch (e) {
        return url;
      }
    }
    
    return url;
  };
  
  if (loading && !formData.name) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </Container>
    );
  }
  
  return (
    <Container className="py-5">
      <Row>
        <Col md={4}>
          <Card className="text-center p-3 mb-4">
            <div className="profile-image-container mb-3">
              {previewUrl ? (
                <img 
                  src={getOptimizedProfileImage(previewUrl)}
                  alt="Profil Fotoğrafı" 
                  className="rounded-circle img-fluid profile-image"
                  style={{ 
                    width: '200px', 
                    height: '200px', 
                    objectFit: 'cover',
                    border: '1px solid #ddd',
                    margin: '0 auto'
                  }}
                />
              ) : (
                <div 
                  className="default-profile-image rounded-circle d-flex align-items-center justify-content-center"
                  style={{ 
                    width: '200px', 
                    height: '200px',
                    backgroundColor: '#f0f0f0',
                    margin: '0 auto'
                  }}
                >
                  <i className="bi bi-person" style={{ fontSize: '5rem', color: '#ccc' }}></i>
                </div>
              )}
            </div>
            
            <Form.Group controlId="profileImage" className="mb-3">
              <Form.Label>Profil Fotoğrafı</Form.Label>
              <Form.Control 
                type="file" 
                accept="image/*"
                onChange={handleImageChange}
              />
              <Form.Text className="text-muted">
                2MB'dan küçük JPG, PNG veya JPEG dosyası yükleyin
              </Form.Text>
            </Form.Group>
          </Card>
        </Col>
        
        <Col md={8}>
          <Card className="p-4">
            <h2 className="mb-4">Profil Bilgilerinizi Düzenleyin</h2>
            
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Ad Soyad</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Adınız Soyadınız"
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>E-posta Adresi</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ornek@mail.com"
                  required
                />
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="phone">
                <Form.Label>Telefon</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="0(5XX) XXX XX XX"
                />
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="address">
                <Form.Label>Adres</Form.Label>
                <Form.Control
                  as="textarea"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Adres bilgileriniz"
                />
              </Form.Group>
              
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Güncelleniyor...' : 'Profili Güncelle'}
                </Button>
                
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/profile')}
                >
                  İptal
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfileEditPage;